import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import paverPatioImg from "@/assets/patio-paver-installation-paramus-nj-2.webp";
import outdoorKitchenImg from "@/assets/custom-masonry-outdoor-kitchen-north-haledon-passaic-county-nj.webp";
import paverDrivewayImg from "@/assets/custom-masonry-driveway-cedar-grove-nj.webp";
import retainingWallImg from "@/assets/ep-henry-stone-retaining-wall-franklin-lakes-nj.webp";

const ACORN_HREF =
  "https://www.acornfinance.com/pre-qualify/?d=0D862&utm_medium=web_pre_qual_banner";
const ACORN_IMG =
  "https://fs.acornfinance.com/banners/acorn-finance-banner-easy-payment-options-horizontal-medium.png";
const ACORN_ALT =
  "Acorn Finance apply and get affordable payment options from multiple lenders";

function AcornBanner() {
  return (
    <a
      href={ACORN_HREF}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="inline-block"
    >
      <img
        id="acornBanner"
        style={{
          margin: "10px",
          padding: "1px",
          border: "1px solid black",
          borderRadius: "5px",
          maxWidth: "100%",
          height: "auto",
        }}
        src={ACORN_IMG}
        alt={ACORN_ALT}
      />
    </a>
  );
}

const steps = [
  {
    title: "Pre-qualify in about 60 seconds",
    body:
      "You fill out a short online form on the Acorn Finance site. The form uses a soft credit check, which means there is no impact to your credit score.",
  },
  {
    title: "Compare offers from multiple lenders",
    body:
      "Acorn works with a network of lenders who compete for your business. You will see pre-qualified offers side by side, so you can pick the rate and term that fits your budget.",
  },
  {
    title: "Finalize and fund",
    body:
      "Once you choose an offer, you finish the application directly with that lender. Most approved customers receive funds within 1-2 business days, and from there you can move forward with your project.",
  },
];

const projectCards = [
  {
    title: "Paver Patios",
    description:
      "A full paver patio with optional features like fire pits or seating walls.",
    href: "/paving-services/paver-patios",
    image: paverPatioImg,
    alt: "Large luxury paver patio with outdoor dining, seating areas, and stone pillars at a home in Paramus, Bergen County, NJ, by Masonry Pro Construction.",
  },
  {
    title: "Outdoor Kitchens & Fireplaces",
    description:
      "Built-in grills, pizza ovens, gas fireplaces, and full outdoor cooking spaces.",
    href: "/masonry-services/outdoor-kitchens",
    image: outdoorKitchenImg,
    alt: "Custom outdoor kitchen in North Haledon, Passaic County, NJ",
  },
  {
    title: "Concrete & Paver Driveways",
    description:
      "New driveway installations and full driveway replacements.",
    href: "/paving-services/paver-driveways",
    image: paverDrivewayImg,
    alt: "Custom masonry driveway completed at a residential property in Cedar Grove, Essex County, NJ, by Masonry Pro Construction.",
  },
  {
    title: "Retaining Walls",
    description:
      "Engineered structural walls and decorative tiered walls for sloped lots.",
    href: "/masonry-services/retaining-walls",
    image: retainingWallImg,
    alt: "Stone retaining wall in Franklin Lakes, NJ by Masonry Pro",
  },
];

const faqs = [
  {
    question: "Will checking my offers hurt my credit score?",
    answer:
      "No. The pre-qualification process uses a soft credit check, which has no impact on your credit score. Only when you choose an offer and move forward with that specific lender does a hard credit pull happen, and at that point you have already seen the rates and terms.",
  },
  {
    question: "Who is Acorn Finance?",
    answer:
      "Acorn Finance is an online platform that connects homeowners with a network of vetted lenders for home improvement projects. They are not a lender themselves. They route your information to multiple lenders and return pre-qualified offers so you can compare rates and terms in one place.",
  },
  {
    question: "How much can I finance for a masonry project?",
    answer:
      "Through Acorn Finance, homeowners can pre-qualify for personal loans up to $100,000, depending on credit profile and lender criteria. Most masonry, paving, and outdoor living projects fall well within that range.",
  },
  {
    question: "How long does it take to get the money?",
    answer:
      "Pre-qualification takes about 60 seconds. After you choose an offer and complete the application directly with the lender, most approved customers receive funds within 1-2 business days.",
  },
  {
    question: "Do I have to use the financing if I pre-qualify?",
    answer:
      "No. Pre-qualifying through Acorn Finance is not a commitment. You are free to review your offers and decide not to move forward, or to pay for your project another way. There is no obligation and no fee to check your options.",
  },
  {
    question: "Can I use a loan for any masonry service you offer?",
    answer:
      "Yes. Loans through Acorn Finance are personal loans paid directly to you, so you can use the funds for any home improvement project we provide, including patios, driveways, retaining walls, outdoor kitchens, fireplaces, walkways, steps, and waterproofing work.",
  },
];

export default function Financing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Project Financing Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Finance your masonry project with Masonry Pro Construction in Totowa, NJ. Compare monthly payment options from multiple lenders in about a minute online." />
        <meta property="og:description" content="Finance your masonry project with Masonry Pro Construction in Totowa, NJ. Compare monthly payment options from multiple lenders in about a minute online." />
        <meta property="og:title" content="Project Financing Totowa, NJ | Masonry Pro Construction" />
        <meta
          property="og:title"
          content="Masonry Project Financing in North Jersey | Masonry Pro"
        />
        <meta
          property="og:description"
          content="Want to start your masonry project now and pay over time? Compare financing offers from multiple lenders in about a minute, with no hit to your credit score."
        />
        <meta property="og:url" content="https://masonry-pro.com/financing" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://masonry-pro.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Financing",
                item: "https://masonry-pro.com/financing",
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.answer,
              },
            })),
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero */}
          <section
            className="relative overflow-hidden"
            style={{ backgroundColor: "#0d1017", paddingTop: "80px", paddingBottom: "80px" }}
          >
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1
                  style={{
                    color: "#f5f5f5",
                    fontSize: "3rem",
                    fontWeight: "bold",
                    lineHeight: 1.1,
                    marginBottom: "1.5rem",
                  }}
                >
                  Masonry Project Financing in{" "}
                  <span style={{ color: "#d4a853" }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground mb-8">
                  A new patio, driveway, or retaining wall is a real investment, and not every project fits neatly into a homeowner's budget all at once. That is why we have partnered with Acorn Finance, so you can compare monthly payment options from multiple lenders in about a minute. Checking your offers will not affect your credit score.
                </p>
                <div className="flex justify-center">
                  <AcornBanner />
                </div>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
                  How Financing Works With{" "}
                  <span className="text-gradient-gold">Acorn</span>
                </h2>
                <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-12">
                  The process is simple, and it can be completed entirely online before you ever sign a contract with us. Here is what to expect.
                </p>
                <div className="grid md:grid-cols-3 gap-8">
                  {steps.map((step, idx) => (
                    <div
                      key={step.title}
                      className="glass-card rounded-xl p-8 text-center"
                    >
                      <div className="w-14 h-14 rounded-full bg-gradient-navy text-secondary text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                        {idx + 1}
                      </div>
                      <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                      <p className="text-muted-foreground">{step.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* What You Can Finance */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
                  Projects You Can Finance With{" "}
                  <span className="text-gradient-gold">Masonry Pro</span>
                </h2>
                <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-12">
                  Financing makes the most sense on larger investment projects, where breaking the cost into manageable monthly payments is easier than paying out of pocket. Below are the projects our customers most often finance, though loans through Acorn can be used for almost any home improvement we provide.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {projectCards.map((card) => (
                    <Link
                      key={card.title}
                      to={card.href}
                      className="group glass-card rounded-xl overflow-hidden flex flex-col hover:border-secondary/50 transition-colors"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={card.image}
                          alt={card.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="text-xl font-bold mb-2 group-hover:text-secondary transition-colors">
                          {card.title}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {card.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Why Financing Makes Sense */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Why Financing Can Work in{" "}
                  <span className="text-gradient-gold">Your Favor</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Most masonry projects are built to last decades, and they tend to add real value to your home and the way you use it. Financing lets you start the project on the timeline you want instead of waiting another season or two while you save up. It can also leave room in your budget for the upgrades that take a project from good to great, like a wider patio, a soldier course border, or a built-in fire feature.
                  </p>
                  <p>
                    And because the loan goes directly to you and not through us, there are no contractor markups or hidden fees baked into the financing. You shop the offers, you choose the lender, and you pay them back on terms you agreed to.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Apply Now CTA */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to See{" "}
                  <span className="text-gradient-gold">Your Options?</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Click below to check your pre-qualified offers in about 60 seconds. There is no obligation, and the soft credit check will not lower your score. Once you have your numbers, give us a call or request a free estimate so we can scope your project.
                </p>
                <div className="flex justify-center">
                  <AcornBanner />
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">
                  Frequently Asked{" "}
                  <span className="text-gradient-gold">Questions</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {faqs.map((faq, idx) => (
                    <div
                      key={idx}
                      className="glass-card rounded-xl p-6 flex flex-col"
                    >
                      <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                      <p className="text-muted-foreground text-base">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Standard Estimate CTA */}
          <CTASection serviceName="Financing" />
        </main>

        <Footer />
      </div>
    </>
  );
}